/**
 * Active workspace store.
 *
 * The id is persisted to localStorage and pushed into the API client, which
 * attaches it as `X-Workspace-Id` on every workspace-scoped request.
 */

import { create } from 'zustand';
import type { WorkspaceRole, WorkspaceSummary } from '../../shared/types';
import { setActiveWorkspaceId } from '../lib/api';
import { clearWorkspaceQueries } from '../lib/query';
import { useSessionStore } from './session';

const STORAGE_KEY = 'pap-workspace';

function readStoredId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredId(id: string | null): void {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

interface WorkspaceState {
  workspaceId: string | null;
  /** Set the active workspace (persists + updates the API client). */
  setWorkspace: (workspaceId: string | null) => void;
  /** Choose the stored workspace if it is still valid, else the first available. */
  syncWithAvailable: (workspaces: WorkspaceSummary[]) => string | null;
  clear: () => void;
}

const initialId = readStoredId();
setActiveWorkspaceId(initialId);

export const useWorkspaceStore = create<WorkspaceState>()((set, get) => ({
  workspaceId: initialId,

  setWorkspace: (workspaceId) => {
    const previous = get().workspaceId;
    if (previous === workspaceId) return;
    if (previous) clearWorkspaceQueries(previous);
    setActiveWorkspaceId(workspaceId);
    writeStoredId(workspaceId);
    set({ workspaceId });
  },

  syncWithAvailable: (workspaces) => {
    const current = get().workspaceId;
    const stillValid = current && workspaces.some((workspace) => workspace.id === current);
    const next = stillValid ? current : (workspaces[0]?.id ?? null);
    if (next !== current) {
      setActiveWorkspaceId(next);
      writeStoredId(next);
      set({ workspaceId: next });
    }
    return next;
  },

  clear: () => {
    setActiveWorkspaceId(null);
    writeStoredId(null);
    set({ workspaceId: null });
  },
}));

/** The full summary for the active workspace, or null. */
export function useActiveWorkspace(): WorkspaceSummary | null {
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const workspaces = useSessionStore((state) => state.workspaces);
  return workspaces.find((workspace) => workspace.id === workspaceId) ?? null;
}

/** The current user's role in the active workspace. */
export function useWorkspaceRole(): WorkspaceRole | null {
  return useActiveWorkspace()?.role ?? null;
}

/** Timezone of the active workspace — every date in the UI is rendered in it. */
export function useWorkspaceTimezone(): string {
  return useActiveWorkspace()?.timezone ?? 'America/Chicago';
}
